import { useCallback, useContext, useEffect, useMemo } from "react";
import { AddButtonLayerContext, LEGACY_OVERLAY_Z_INDEX } from "../add-buttons/AddButtonLayerContext";
import { Z_INDEX } from "../../core/utils/zIndexScale";

const noop = () => {};
const EMPTY_OVERRIDES = Object.freeze({});
const EMPTY_METADATA = Object.freeze({});

export const useAddButtonLayer = (layerInstanceId, options = {}) => {
  const context = useContext(AddButtonLayerContext);
  const {
    layerToken = "default",
    mode = "overlay",
    // IMPORTANT: do NOT default these to `{}` inline.
    // A new object each render causes the overlay registration effect to re-run,
    // which can cascade into unnecessary state updates (and, in some cases, update loops).
    overrides = EMPTY_OVERRIDES,
    metadata = EMPTY_METADATA,
  } = options;

  const token = context.tokens[layerToken] || context.tokens.default;

  const config = useMemo(
    () => ({
      ...token,
      ...overrides,
    }),
    [token, overrides],
  );

  useEffect(() => {
    if (mode !== "overlay") return noop;
    // IMPORTANT: do NOT depend on the full `context` object here.
    // The provider recreates the context value when `activeOverlayId` changes, and if we
    // include `context` in deps we'll constantly re-run this effect. That re-run triggers
    // cleanup which can call `unregisterOverlay`, potentially resetting active state and
    // causing update loops / "Maximum update depth exceeded" in the editor.
    context.registerOverlay(layerInstanceId, { layerToken, config, metadata });
    return () => context.unregisterOverlay(layerInstanceId);
  }, [
    layerInstanceId,
    layerToken,
    config,
    metadata,
    mode,
    context.registerOverlay,
    context.unregisterOverlay,
  ]);

  const layeringEnabled = context.featureFlags.enableAddButtonLayering !== false;
  const isActive = context.activeOverlayId === layerInstanceId;

  const wrapperZ = layeringEnabled
    ? isActive
      ? config.wrapperZ + 2
      : config.wrapperZ
    : config.legacyWrapperZ ?? LEGACY_OVERLAY_Z_INDEX;

  const buttonBaseZ = layeringEnabled ? config.buttonBaseZ : Z_INDEX.ADD_BUTTON_BASE;
  const buttonHoverZ = layeringEnabled ? config.buttonHoverZ : Z_INDEX.ADD_BUTTON_HOVER;
  const indicatorZ = layeringEnabled ? config.indicatorZ : Z_INDEX.EDIT_INDICATOR;
  const buttonZ = isActive ? buttonHoverZ : buttonBaseZ;

  const activate = useCallback(
    (metaOverride) => {
      context.activateOverlay(layerInstanceId, metaOverride || metadata);
    },
    [context, layerInstanceId, metadata],
  );

  const deactivate = useCallback(() => {
    context.deactivateOverlay(layerInstanceId);
  }, [context, layerInstanceId]);

  return {
    layerId: layerInstanceId,
    layerToken,
    config,
    wrapperZ,
    buttonZ,
    hoverZ: buttonHoverZ,
    indicatorZ,
    isActive,
    layeringEnabled,
    activate,
    deactivate,
  };
};

export default useAddButtonLayer;




